import React from 'react';
import styles from '../styles/styles.module.css';

import RegularInputFields from './RegularInputFields';
import ChangePasswordFields from './ChangePasswordFields';
import { signIn, confirmSignIn } from '@aws-amplify/auth';
import { AppState } from '../../../Redux/store';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  setErrorMessage,
  setIsLoading,
  setNewPasswordRequired,
  setPassword,
  setPassword1,
  setPassword2,
  setQrCodeUrl,
  setShowMfaInput,
} from '../../../Redux/slices/authSlice';

const RegularLogin: React.FC = () => {
  const {
    errorMessage,
    email,
    password,
    newPasswordRequired,
    password1,
    password2,
    isLoading,
    qrCodeUrl,
  } = useSelector((state: AppState) => state.auth);
  const dispatch = useDispatch();

  async function handleLogin() {
    try {
      dispatch(setIsLoading(true));
      dispatch(setErrorMessage(''));
      const auth = await signIn({
        username: email,
        password: password,
      });

      const { nextStep } = auth;
      console.log(nextStep);
      if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // Get the URI for QR code
        const setupUri = nextStep.totpSetupDetails.getSetupUri(
          email,
          process.env.NEXT_PUBLIC_COGNITO_APP_NAME
        );

        dispatch(setQrCodeUrl(setupUri.href));
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        dispatch(setShowMfaInput(true));
      } else if (
        nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        dispatch(setNewPasswordRequired(true));
      }
    } catch (err) {
      console.log(err);
      const errorMessage =
        (err as Error).message || 'An unknown error occurred';
      dispatch(setErrorMessage(errorMessage));
    } finally {
      dispatch(setIsLoading(false));
    }
  }

  function handleContinue() {
    dispatch(setShowMfaInput(true));
    dispatch(setQrCodeUrl(null));
  }

  async function handleUpdatePassword() {
    dispatch(setIsLoading(true));
    if (password1 !== password2) {
      dispatch(setErrorMessage('Passwords do not match'));
      dispatch(setIsLoading(false));
      return;
    }

    dispatch(setErrorMessage(''));

    try {
      // Confirm new password
      await confirmSignIn({
        challengeResponse: password1,
      });

      // Clear states and proceed to next step
      dispatch(setNewPasswordRequired(false));
      dispatch(setErrorMessage(''));
    } catch (err) {
      if (
        (err as Error).message.toLocaleLowerCase().includes('invalid session')
      ) {
        //reset the state if the session is expired
        dispatch(setNewPasswordRequired(false));
        dispatch(setQrCodeUrl(null));
        dispatch(setPassword(''));
        dispatch(setPassword1(''));
        dispatch(setPassword2(''));
        dispatch(setShowMfaInput(false));
        dispatch(setErrorMessage('Session expired, please start over'));
      } else {
        const errorMessage =
          (err as Error).message ||
          'An unknown error occurred, please refresh the page';
        dispatch(setErrorMessage(errorMessage));
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  }

  function returnButtons() {
    if (newPasswordRequired) {
      return (
        <button
          disabled={isLoading}
          onClick={handleUpdatePassword}
          className={styles.button}
        >
          {isLoading ? 'Updating password...' : 'Change Password'}
        </button>
      );
    } else {
      if (qrCodeUrl) {
        return (
          <button
            disabled={isLoading}
            onClick={handleContinue}
            className={styles.button}
          >
            {isLoading ? 'Please wait...' : 'Continue'}
          </button>
        );
      } else {
        return (
          <button
            disabled={isLoading}
            onClick={handleLogin}
            className={styles.button}
          >
            {isLoading ? 'Please wait...' : 'Log in'}
          </button>
        );
      }
    }
  }

  return (
    <>
      {newPasswordRequired ? <ChangePasswordFields /> : <RegularInputFields />}
      <div className={styles.errorContainer}>
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </div>

      <div className={styles.buttonContainer}>{returnButtons()}</div>
    </>
  );
};

export default RegularLogin;
